/**
 * The report filter bar in the DECK's order, with the deck's hidden filters left out.
 *
 * Prophecy's WW Report shows Version, Deck, Channel, Adjustment, Allocation, Scale, Currency — view
 * controls and dimension filters interleaved — and no Country or Product chip, although both still
 * scope the numbers. The screen config states both facts per entry (`position`, `hidden`), so the
 * bar is ordered by the entry each chip came from. A chip with no position (a mandatory default the
 * screen never listed) keeps its place after the positioned ones, in its original order.
 *
 * Pure + React-free (CLAUDE.md separation of concerns).
 */

import type { ReportFilter, ViewControl } from 'src/components/star-grid/api/reportEngineTypes';

export type FilterBarItem =
  | { readonly kind: 'control'; readonly control: ViewControl }
  | { readonly kind: 'filter'; readonly filter: ReportFilter; readonly index: number };

/** The filters that get a chip. Hidden ones still reach the query through the spec. */
export const visibleReportFilters = (filters: readonly ReportFilter[]): ReportFilter[] => filters.filter((filter) => !filter.hidden);

export const orderFilterBarItems = (controls: readonly ViewControl[], visibleFilters: readonly ReportFilter[]): FilterBarItem[] => {
  const items: { readonly item: FilterBarItem; readonly position: number; readonly original: number }[] = [
    ...controls.map((control, i) => ({ item: { kind: 'control' as const, control }, position: control.position ?? Infinity, original: i })),
    ...visibleFilters.map((filter, index) => ({
      item: { kind: 'filter' as const, filter, index },
      position: filter.position ?? Infinity,
      original: controls.length + index
    }))
  ];
  return items.sort((a, b) => a.position - b.position || a.original - b.original).map((entry) => entry.item);
};
